<template>
  <div
    ref="root"
    class="oct-popover"
  >
    <!-- Content -->
    <div
      ref="anchor"
      class="oct-popover__anchor"
      @click.stop="$_octPopover_onAnchorClick"
    >
      <slot />
    </div>

    <!-- Popper -->
    <div
      v-show="$data.$_octPopover_open"
      ref="popper"
      class="oct-popover__popper"
      :style="$data.$_octPopover_popperStyle"
      @click.stop="$_octPopover_onPopperClick"
    >
      <slot name="popper" />
    </div>
  </div>
</template>

<script>
const VERT = ['top', 'center', 'bottom']
const HORIZ = ['left', 'center', 'right']

function pickVertical (v, fallback) {
  return VERT.indexOf(v) !== -1 ? v : fallback
}

function pickHorizontal (h, fallback) {
  return HORIZ.indexOf(h) !== -1 ? h : fallback
}

export default {
  name: 'OctPopover',
  props: {
    /**
     * Which point on the anchor element the popper aligns to.
     * @typedef {{ vertical: 'top'|'center'|'bottom', horizontal: 'left'|'center'|'right' }} Origin
     */
    anchorOrigin: {
      type: Object,
      default () {
        return {
          vertical: 'bottom',
          horizontal: 'left',
        }
      },
      validator (value) {
        if (!value || typeof value !== 'object') return false
        const vOk = value.vertical == null || VERT.indexOf(value.vertical) !== -1
        const hOk = value.horizontal == null || HORIZ.indexOf(value.horizontal) !== -1
        return vOk && hOk
      },
    },
    /**
     * Which point on the popper surface is placed at the anchor origin.
     */
    transformOrigin: {
      type: Object,
      default () {
        return {
          vertical: 'top',
          horizontal: 'left',
        }
      },
      validator (value) {
        if (!value || typeof value !== 'object') return false
        const vOk = value.vertical == null || VERT.indexOf(value.vertical) !== -1
        const hOk = value.horizontal == null || HORIZ.indexOf(value.horizontal) !== -1
        return vOk && hOk
      },
    },
    /**
     * When true, a click anywhere on the popper surface closes the popover.
     */
    closeOnPopperClick: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      $_octPopover_open: false,
      $_octPopover_popperStyle: {},
    }
  },
  computed: {
    $_octPopover_resolvedAnchorOrigin () {
      const o = this.anchorOrigin || {}
      return {
        vertical: pickVertical(o.vertical, 'bottom'),
        horizontal: pickHorizontal(o.horizontal, 'left'),
      }
    },
    $_octPopover_resolvedTransformOrigin () {
      const o = this.transformOrigin || {}
      return {
        vertical: pickVertical(o.vertical, 'top'),
        horizontal: pickHorizontal(o.horizontal, 'left'),
      }
    },
  },
  watch: {
    '$data.$_octPopover_open' (isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.$_octPopover_bindGlobalListeners()
        })
      } else {
        this.$_octPopover_unbindGlobalListeners()
      }
    },
    anchorOrigin: {
      deep: true,
      handler () {
        if (this.$data.$_octPopover_open) this.$nextTick(() => this.$_octPopover_updatePosition())
      },
    },
    transformOrigin: {
      deep: true,
      handler () {
        if (this.$data.$_octPopover_open) this.$nextTick(() => this.$_octPopover_updatePosition())
      },
    },
  },
  beforeDestroy () {
    this.$_octPopover_unbindGlobalListeners()
  },
  methods: {
    $_octPopover_originOffsetY (vertical, height) {
      if (vertical === 'center') return height / 2
      if (vertical === 'bottom') return height
      return 0
    },
    $_octPopover_originOffsetX (horizontal, width) {
      if (horizontal === 'center') return width / 2
      if (horizontal === 'right') return width
      return 0
    },
    $_octPopover_updatePosition () {
      if (!this.$data.$_octPopover_open) return

      const root = this.$refs.root
      const anchor = this.$refs.anchor
      const popper = this.$refs.popper

      if (!root || !anchor || !popper) return

      const rootRect = root.getBoundingClientRect()
      const anchorRect = anchor.getBoundingClientRect()
      const popperRect = popper.getBoundingClientRect()

      const ao = this.$_octPopover_resolvedAnchorOrigin
      const to = this.$_octPopover_resolvedTransformOrigin

      const anchorX =
        anchorRect.left -
        rootRect.left +
        this.$_octPopover_originOffsetX(ao.horizontal, anchorRect.width)
      const anchorY =
        anchorRect.top -
        rootRect.top +
        this.$_octPopover_originOffsetY(ao.vertical, anchorRect.height)

      const transformX = this.$_octPopover_originOffsetX(to.horizontal, popperRect.width)
      const transformY = this.$_octPopover_originOffsetY(to.vertical, popperRect.height)

      let left = anchorX - transformX

      const popperViewportLeft = rootRect.left + left
      const popperViewportRight = popperViewportLeft + popperRect.width
      const viewportWidth = window.innerWidth
      const overflowsLeft = popperViewportLeft < 0
      const overflowsRight = popperViewportRight > viewportWidth

      if (overflowsLeft && !overflowsRight) {
        left = -rootRect.left
      } else if (!overflowsLeft && overflowsRight) {
        left = viewportWidth - rootRect.left - popperRect.width
      }

      this.$data.$_octPopover_popperStyle = {
        left: `${left}px`,
        top: `${anchorY - transformY}px`,
      }
    },
    $_octPopover_onAnchorClick () {
      this.$data.$_octPopover_open = true
      this.$nextTick(() => this.$_octPopover_updatePosition())
    },
    $_octPopover_onPopperClick () {
      if (this.closeOnPopperClick) {
        this.$data.$_octPopover_open = false
      }
    },
    $_octPopover_onDocumentPointerDown (event) {
      const root = this.$refs.root
      if (!root || !this.$data.$_octPopover_open) return
      const target = event.target
      if (root.contains(target)) return
      this.$data.$_octPopover_open = false
    },
    $_octPopover_onWindowResize () {
      if (this.$data.$_octPopover_open) this.$_octPopover_updatePosition()
    },
    $_octPopover_onWindowScroll () {
      if (this.$data.$_octPopover_open) this.$_octPopover_updatePosition()
    },
    $_octPopover_bindGlobalListeners () {
      document.addEventListener('mousedown', this.$_octPopover_onDocumentPointerDown, true)
      window.addEventListener('resize', this.$_octPopover_onWindowResize)
      window.addEventListener('scroll', this.$_octPopover_onWindowScroll, true)
    },
    $_octPopover_unbindGlobalListeners () {
      document.removeEventListener('mousedown', this.$_octPopover_onDocumentPointerDown, true)
      window.removeEventListener('resize', this.$_octPopover_onWindowResize)
      window.removeEventListener('scroll', this.$_octPopover_onWindowScroll, true)
    },
  },
}
</script>

<style scoped lang="scss">
.oct-popover {
  position: relative;
  display: inline-block;
  vertical-align: middle;
}

.oct-popover__anchor {
  cursor: pointer;
}

.oct-popover__popper {
  position: absolute;
  z-index: 1;
  box-sizing: border-box;
}
</style>
